/**
 * Converts a camelCase or PascalCase string to snake_case.
 *
 * @example
 * toSnakeCase('someKey') // 'some_key'
 * toSnakeCase('SomeValue') // 'some_value'
 */
const toSnakeCase = (key: string): string =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1_$2')
    .toLowerCase()

/**
 * Recursively converts all object keys from camelCase to snake_case
 * while preserving values.
 * @param input - Any value (object, array, primitive)
 * @returns A new structure with snake_case keys
 */
export function keysToSnakeCase<T>(input: T): T {
  if (Array.isArray(input)) {
    return input.map(keysToSnakeCase) as T;
  }

  if (input !== null && typeof input === 'object') {
    return Object.entries(input).reduce((acc, [key, value]) => {
      const snakeKey = toSnakeCase(key);
      (acc as Record<string, unknown>)[snakeKey] = keysToSnakeCase(value);
      return acc
    }, {} as T);
  }

  return input;
}
